import type { Migration, MigrationDevice } from '../api/migrations';
import type { StateName } from '../theme/tokens';

// What does an operator call this campaign's state (FD-457)?
//
// `Migrations.Status` is NOT the answer: it is written `"active"` at creation and
// no writer in the codebase ever updates it (FD-269), so reading it would give
// every campaign the same word forever. The real lifecycle lives in `bprtStatus`
// — the wave credential — plus the terminal outcomes of the devices.

export interface CampaignStatusView {
  label: string;
  state: StateName;
  /** A one-line explanation for a tooltip or caption. */
  detail: string;
}

const TERMINAL_OK = 'completed';

export function deriveCampaignStatus(
  migration: Pick<Migration, 'bprtStatus'>,
  devices: Pick<MigrationDevice, 'status' | 'terminalOutcome'>[] = [],
): CampaignStatusView {
  const bprt = (migration.bprtStatus ?? '').trim().toLowerCase();

  if (bprt === '' || bprt === 'notactivated') {
    return {
      label: 'Not activated',
      state: 'neutral',
      detail: 'No wave credential has been minted, so no device can be delivered a flight plan.',
    };
  }

  if (bprt === 'active') {
    return {
      label: 'Active',
      state: 'progress',
      detail: 'The wave credential is live and devices can fetch their flight plans.',
    };
  }

  if (bprt === 'expired') {
    return {
      label: 'Expired',
      state: 'warning',
      detail: 'The wave credential has passed its expiry; re-activate to continue.',
    };
  }

  if (bprt === 'revoked') {
    const terminal = devices.filter(
      (d) => (d.terminalOutcome ?? '').trim() !== '' || isTerminalStatus(d.status),
    );
    const failures = terminal.filter(
      (d) => (d.terminalOutcome ?? d.status ?? '').trim().toLowerCase() !== TERMINAL_OK,
    );
    if (devices.length > 0 && failures.length > 0) {
      return {
        label: 'Completed with failures',
        state: 'warning',
        detail: `${failures.length} of ${devices.length} devices did not complete.`,
      };
    }
    return {
      label: 'Completed',
      state: 'success',
      detail: 'The wave credential was revoked after every assigned device finished.',
    };
  }

  // An unfamiliar value is rendered VERBATIM and neutral rather than mapped to a
  // default — the `vocabulary.ts` rule. A campaign whose state the console does
  // not understand should look unfamiliar; labelling it "Active" would hide a
  // real data problem behind a plausible word.
  return {
    label: migration.bprtStatus ?? 'Unknown',
    state: 'neutral',
    detail: 'The console does not recognise this wave state.',
  };
}

function isTerminalStatus(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return s === 'completed' || s === 'aborted' || s === 'salvaged' || s === 'faulted';
}

/** The device lifecycle word, as a pill state. */
export function deviceStatusState(
  status: string | null | undefined,
  terminalOutcome?: string | null,
): StateName {
  const value = (terminalOutcome ?? status ?? '').trim().toLowerCase();
  switch (value) {
    case 'completed':
      return 'success';
    case 'in-progress':
    case 'initialized':
      return 'progress';
    case 'salvaged':
    case 'aborted':
      return 'warning';
    case 'faulted':
      return 'critical';
    default:
      return 'neutral';
  }
}
